'use strict';

/**
 * Generic module to handle uploading files to Studio.
 * Looks like a button that triggers a file dialog and autosubmits the form after selecting file.
 * Supports single upload only.
 */

class Uploader {

  constructor(options) {
    const settings = {
      uploadInputSelector: '.uploader input[name="file"]',
      allowedTypes: [
        // Images
        'image/jpeg', 'image/png', 'image/heic', 'image/heif',
        // Documents
        'application/pdf',
        'application/msword', // .doc
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // .docx
        'application/vnd.oasis.opendocument.text', // .odt
        'text/plain', // .txt
        // Spreadsheets
        'application/vnd.ms-excel', // .xls
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
        'application/vnd.oasis.opendocument.spreadsheet', // .ods
      ],
      allowedExtensions: ['.doc', '.docx', '.odt', '.txt', '.pdf', '.ods', '.xls', '.xlsx', '.jpg', '.jpeg', '.gif', '.png', '.heic', '.heif'],
    };

    Object.assign(this, settings, options);
  }

  /**
   * Checks if the file type is allowed
   *
   * @param {File} file - The file to validate
   * @returns {boolean} - True if file type is allowed or no restrictions set
   */
  _isAllowedType(file) {
    // If no restrictions specified, all types are allowed
    if ((!this.allowedTypes || this.allowedTypes.length === 0) &&
        (!this.allowedExtensions || this.allowedExtensions.length === 0)) {
      return true;
    }

    // Check MIME type
    if (this.allowedTypes && this.allowedTypes.includes(file.type)) {
      return true;
    }

    // Fallback: check file extension (MIME type can be empty or unreliable)
    if (this.allowedExtensions) {
      const fileName = file.name.toLowerCase();
      return this.allowedExtensions.some(ext => fileName.endsWith(ext));
    }

    return false;
  }

  /**
   * Attaches events if currently on an enabled page
   */
  init() {
    if(document.querySelectorAll(this.uploadInputSelector).length <= 0) {
      // short circuit initialization, as we're on a page that doesn't need this module.
      return;
    }

    // Initialize upload button
    this.uploadInput = document.querySelector(this.uploadInputSelector);
    if(this.uploadInput) {
      this.uploadInput.addEventListener('change', this._handleUploadInputChange.bind(this));
    }
  }

  /**
   * Trigger upload when user selects an image
   *
   * @param {object} event - The file input's change event
   */
  _handleUploadInputChange(event) {
    if(event.target.files.length > 0) {
      const file = event.target.files[0];

      if (!this._isAllowedType(file)) {
        // Reset the input so user can select again
        event.target.value = '';

        console.warn(`Skipped invalid file types: ${file.name}`);
        alert(`Seuraava tiedosto ohitettiin, koska se ei ole sallittua tiedostotyyppiä: ${file.name}`);
        return;
      }

      this._upload(event.target);
    }
  }

  /**
   * Submits the form
   *
   * @param {HTMLFileInputElement} input - the file input element
   */
  _upload(input) {
    if(input.files.length > 0) {
      input.form.submit();
    }
  }
}

module.exports = Uploader;
